import React, { useMemo } from 'react';
import MessageAgent from './MessageAgent';
import Box from '@mui/material/Box';
import { useMediaQuery, useTheme } from '@mui/material';
import type { IArtifactService } from '@lib/services/interfaces/artifactService';
import { useAiChatSessionStore } from '../AiChatContext';
import type { EventBusService } from '@lib/services/eventBusService';
import ErrorBondary from '@lib/ErrorBondary';
import SubAgentBox from './SubAgentBox';


interface CurrentMessage {
  onLinkClicked: (link: string, content: string | null) => void;
  messagesRef: React.RefObject<Map<string, HTMLDivElement>>;
  artifactService: IArtifactService;
  eventBusService: EventBusService;
  friendlyMode?: boolean;
}

const CurrentMessage: React.FC<CurrentMessage> = ({ onLinkClicked, eventBusService, messagesRef, artifactService, friendlyMode }) => {
  const turnOpen = useAiChatSessionStore((state) => state.turnOpen);
  const message = useAiChatSessionStore((state) => state.currentMessage);

  if (!message) {
    return null;
  }

  return (
    <ErrorBondary>
      <Box
        sx={{
          p: 0.5,
          width: '100%',
          height: '100%',
          // Streaming-content arrival animation. CSS animations only
          // fire on initial mount of an element (or when animation-name
          // changes); React keeps stable DOM identity for existing
          // markdown nodes across partials, so this plays exactly once
          // per newly-rendered chunk — a new <tr> when the table grows,
          // a new <li> when the bullet list extends, a new <p> when
          // the text rolls past a paragraph break. Existing nodes that
          // merely gain trailing text don't re-fire, so character-by-
          // character text inside an existing <p> still flows without
          // re-animating. The `both` fill-mode ensures the element
          // looks correct before and after the keyframe runs.
          '@keyframes currentMessageFadeIn': {
            '0%': { opacity: 0, transform: 'translateY(4px)' },
            '100%': { opacity: 1, transform: 'translateY(0)' },
          },
          // Curated selector list rather than `& *`: layout wrappers
          // (Stack / Box / generic divs) shouldn't fade in — they're
          // structural, not content. Targeting block-level *content*
          // tags keeps the animation tied to what the LLM actually
          // streams.
          // Excluded from the animated selector list (would blink):
          //   - `<code>` (inline): sibling-shift inside its parent
          //     gives it a new identity on partial re-parses.
          //   - `<blockquote>`: GFM keeps re-deciding whether the
          //     next `> ` line extends the existing quote or opens
          //     a new one, so the wrapper element gets re-mounted
          //     each chunk.
          //   - any descendant of `[data-live-jsx-content]`: the
          //     compiled Component inside LiveJsxBlock re-mounts
          //     on every code/scope change, which would replay
          //     this keyframe on the headings/paragraphs inside
          //     a metric/chart card and read as a blink. Cards
          //     have their own arrival animation upstream.
          //   - any descendant of `[data-ui-tool-content]`: a host
          //     UI render tool (e.g. present_tripstretch) draws a
          //     rich MUI card whose `<Typography>` (→ `<p>`/`<h*>`)
          //     and logo `<img>` nodes mount all at once when the
          //     tool result folds in (status Done) — typically right
          //     as the turn ends — so the fade would replay across
          //     the whole card and read as a blink.
          '& p:not([data-live-jsx-content] *):not([data-ui-tool-content] *), & li:not([data-live-jsx-content] *):not([data-ui-tool-content] *), & tr:not([data-live-jsx-content] *):not([data-ui-tool-content] *), & pre:not([data-live-jsx-content] *):not([data-ui-tool-content] *), & h1:not([data-live-jsx-content] *):not([data-ui-tool-content] *), & h2:not([data-live-jsx-content] *):not([data-ui-tool-content] *), & h3:not([data-live-jsx-content] *):not([data-ui-tool-content] *), & h4:not([data-live-jsx-content] *):not([data-ui-tool-content] *), & h5:not([data-live-jsx-content] *):not([data-ui-tool-content] *), & h6:not([data-live-jsx-content] *):not([data-ui-tool-content] *), & hr:not([data-live-jsx-content] *):not([data-ui-tool-content] *), & img:not([data-live-jsx-content] *):not([data-ui-tool-content] *)': {
            animation: 'currentMessageFadeIn 220ms ease-out both',
          },
          // Streaming-only left-alignment for table cells. GFM
          // sets text-align inline (style="text-align: center")
          // when the markdown carries `|:---:|`, and `<th>` is
          // center-by-default in browsers. Either way, characters
          // arriving in a centred cell visually push outward from
          // the centre. Force left while the message is still
          // being streamed inside CurrentMessage; once the turn
          // closes and the message moves to the persisted history
          // (rendered outside this Box), the inline/default
          // alignment returns. `!important` is required because
          // GFM's inline `style` attribute outranks normal CSS.
          '& td, & th': {
            textAlign: 'left !important',
          },
          '@media (prefers-reduced-motion: reduce)': {
            '& p:not([data-live-jsx-content] *):not([data-ui-tool-content] *), & li:not([data-live-jsx-content] *):not([data-ui-tool-content] *), & tr:not([data-live-jsx-content] *):not([data-ui-tool-content] *), & pre:not([data-live-jsx-content] *):not([data-ui-tool-content] *), & h1:not([data-live-jsx-content] *):not([data-ui-tool-content] *), & h2:not([data-live-jsx-content] *):not([data-ui-tool-content] *), & h3:not([data-live-jsx-content] *):not([data-ui-tool-content] *), & h4:not([data-live-jsx-content] *):not([data-ui-tool-content] *), & h5:not([data-live-jsx-content] *):not([data-ui-tool-content] *), & h6:not([data-live-jsx-content] *):not([data-ui-tool-content] *), & hr:not([data-live-jsx-content] *):not([data-ui-tool-content] *), & img:not([data-live-jsx-content] *):not([data-ui-tool-content] *)': {
              animation: 'none',
            },
          },
        }}
      >
        {message && (
          <MessageAgent showSubAgents={true} onGoing={true} container='currentMessage' isConnectionOpen={turnOpen} message={message} onLinkClicked={onLinkClicked} appendWaitingPart={(turnOpen)} messagesRef={messagesRef} preview={true} friendlyMode={friendlyMode} />
        )}
      </Box>
    </ErrorBondary>
  );
};

export default CurrentMessage;
